import {
    Card,
    CardContent,
    Chip,
    FormControl,
    Grid,
    MenuItem,
    Select,
    SelectChangeEvent,
    Skeleton,
    Stack,
    Typography,
} from '@mui/material';
import { LocalizationProvider, MobileDateRangePicker } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { OnlyFansSubscriptionsUserType } from '../../../../hooks/useSubscriber';
import useSubscriberUsersCount from '../../../../hooks/useSubscriberUsersCount';

type RecentPeriod = '1d' | '3d' | '7d' | '14d' | '21d' | '1m' | '3m' | '6m' | 'custom';

const dateRangeToRecentPeriod = (fromDate: Date | null, toDate: Date | null): RecentPeriod => {
    if (!fromDate || !toDate) {
        return '1m';
    }

    const now = moment();
    const fromMoment = moment(fromDate);
    const toMoment = moment(toDate);

    const diffDays = toMoment.diff(fromMoment, 'days');
    const diffMonths = toMoment.diff(fromMoment, 'months');

    if (toMoment.isSame(now, 'day')) {
        switch (diffDays) {
            case 1:
                return '1d';
            case 3:
                return '3d';
            case 7:
                return '7d';
            case 14:
                return '14d';
            case 21:
                return '21d';
            default:
                break;
        }
    }

    if (toMoment.isSame(now, 'month')) {
        switch (diffMonths) {
            case 1:
                return '1m';
            case 3:
                return '3m';
            case 6:
                return '6m';
            default:
                break;
        }
    }

    return 'custom';
};

type Props = {
    usersType: OnlyFansSubscriptionsUserType;
    targetRecentFansDateFrom: Date | null;
    setTargetRecentFansDateFrom: (date: Date | null) => void;
    targetRecentFansDateTo: Date | null;
    setTargetRecentFansDateTo: (date: Date | null) => void;
    session?: any;
};

const MessageUsersFormTargetRecentFans = (props: Props) => {
    const { usersType, targetRecentFansDateFrom, setTargetRecentFansDateFrom, targetRecentFansDateTo, setTargetRecentFansDateTo, session } =
        props;

    const isInitialized = useRef(false);

    // Calculate initial recentPeriod based on session
    const getInitialRecentPeriod = (): RecentPeriod => {
        if (session?.payload?.targetRecentFansDateFrom && session?.payload?.targetRecentFansDateTo) {
            return dateRangeToRecentPeriod(
                new Date(session.payload.targetRecentFansDateFrom),
                new Date(session.payload.targetRecentFansDateTo),
            );
        }
        return '1m';
    };

    const [recentPeriod, setRecentPeriod] = useState<RecentPeriod>(getInitialRecentPeriod);

    const { data: usersCount, isLoading: usersCountLoading } = useSubscriberUsersCount(
        usersType,
        targetRecentFansDateFrom,
        targetRecentFansDateTo,
    );

    // Initialize dates on mount
    useEffect(() => {
        if (isInitialized.current) return;

        if (session?.payload?.targetRecentFansDateFrom && session?.payload?.targetRecentFansDateTo) {
            setTargetRecentFansDateFrom(new Date(session.payload.targetRecentFansDateFrom));
            setTargetRecentFansDateTo(new Date(session.payload.targetRecentFansDateTo));
        } else {
            // Set default '1m' dates
            const now = moment();
            const fromDate = moment().subtract(1, 'months').startOf('day').toDate();
            setTargetRecentFansDateFrom(fromDate);
            setTargetRecentFansDateTo(now.toDate());
        }

        isInitialized.current = true;
    }, []); // Empty deps - only run once on mount

    // Update dates when recentPeriod changes (but not on initial mount)
    useEffect(() => {
        if (!isInitialized.current) return; // Skip on initial mount
        if (recentPeriod === 'custom') return; // Don't update dates for custom period

        const now = moment();
        let fromDate: Date | null = null;

        switch (recentPeriod) {
            case '1d':
                fromDate = moment().subtract(1, 'days').startOf('day').toDate();
                break;
            case '3d':
                fromDate = moment().subtract(3, 'days').startOf('day').toDate();
                break;
            case '7d':
                fromDate = moment().subtract(7, 'days').startOf('day').toDate();
                break;
            case '14d':
                fromDate = moment().subtract(14, 'days').startOf('day').toDate();
                break;
            case '21d':
                fromDate = moment().subtract(21, 'days').startOf('day').toDate();
                break;
            case '1m':
                fromDate = moment().subtract(1, 'months').startOf('day').toDate();
                break;
            case '3m':
                fromDate = moment().subtract(3, 'months').startOf('day').toDate();
                break;
            case '6m':
                fromDate = moment().subtract(6, 'months').startOf('day').toDate();
                break;
        }

        if (fromDate) {
            setTargetRecentFansDateFrom(fromDate);
            setTargetRecentFansDateTo(now.toDate());
        }
    }, [recentPeriod, setTargetRecentFansDateFrom, setTargetRecentFansDateTo]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            setTargetRecentFansDateFrom(null);
            setTargetRecentFansDateTo(null);
        };
    }, [setTargetRecentFansDateFrom, setTargetRecentFansDateTo]);

    return (
        <>
            <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center" sx={{ marginBottom: 1 }}>
                <Grid item xs={12}>
                    <Card variant="elevation">
                        <CardContent>
                            <Grid container spacing={1} flexGrow={0} alignItems="center" justifyContent="center">
                                <Grid item xs={12}>
                                    <Typography variant="h6" sx={{ marginBottom: 1 }}>
                                        Recently Followed Fans Period
                                    </Typography>
                                </Grid>
                            </Grid>

                            <Grid container spacing={2} flexGrow={0} alignItems="center" justifyContent="center" sx={{ marginBottom: 1 }}>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <Select<RecentPeriod>
                                            value={recentPeriod}
                                            onChange={(event: SelectChangeEvent<RecentPeriod>) =>
                                                setRecentPeriod(event.target.value as RecentPeriod)
                                            }
                                            displayEmpty
                                            fullWidth
                                        >
                                            <MenuItem value="1d">Last 1 day</MenuItem>
                                            <MenuItem value="3d">Last 3 days</MenuItem>
                                            <MenuItem value="7d">Last 7 days</MenuItem>
                                            <MenuItem value="14d">Last 14 days</MenuItem>
                                            <MenuItem value="21d">Last 21 days</MenuItem>
                                            <MenuItem value="1m">Last 1 month</MenuItem>
                                            <MenuItem value="3m">Last 3 months</MenuItem>
                                            <MenuItem value="6m">Last 6 months</MenuItem>
                                            <MenuItem value="custom">Custom</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                {recentPeriod === 'custom' && (
                                    <Grid item xs={12}>
                                        <LocalizationProvider
                                            dateAdapter={AdapterMoment}
                                            localeText={{ start: 'From Date', end: 'To Date' }}
                                        >
                                            <MobileDateRangePicker
                                                value={[moment(targetRecentFansDateFrom), moment(targetRecentFansDateTo)]}
                                                onChange={newValue => {
                                                    if (newValue && newValue.length === 2) {
                                                        setTargetRecentFansDateFrom(newValue[0]?.toDate() || null);
                                                        setTargetRecentFansDateTo(newValue[1]?.toDate() || null);
                                                    }
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography component={'span'} variant="body2" color="textSecondary">
                                            Target users found:
                                        </Typography>
                                        {usersCount && !usersCountLoading && (
                                            <Chip variant="filled" color="success" label={(usersCount.count || 0).toLocaleString()} />
                                        )}
                                        {usersCountLoading && <Skeleton variant="circular" width={40} height={32} />}
                                    </Stack>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </>
    );
};

export default MessageUsersFormTargetRecentFans;
